/**
 * Native <dialog> with focus trap + focus return (P2 §3.10), and a promise-based confirm() replacement.
 * Never window.confirm/alert (India anti-pattern).
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';

export function Dialog({ open, onClose, title, children, footer, wide }: { open: boolean; onClose: () => void; title: ReactNode; children: ReactNode; footer?: ReactNode; wide?: boolean }) {
  const ref = useRef<HTMLDialogElement>(null);
  const opener = useRef<Element | null>(null);
  useEffect(() => {
    const d = ref.current; if (!d) return;
    if (open && !d.open) { opener.current = document.activeElement; d.showModal(); }
    if (!open && d.open) { d.close(); (opener.current as HTMLElement | null)?.focus?.(); }
  }, [open]);
  return (
    <dialog ref={ref} className={['app-dialog', 'stg', 'theme-dark', wide && 'app-dialog--wide'].filter(Boolean).join(' ')} onCancel={(e) => { e.preventDefault(); onClose(); }} onClose={() => open && onClose()}>
      {open && (
        <>
          <header className="app-dialog__head"><h2>{title}</h2><button type="button" className="stg-iconbtn" aria-label="Close" onClick={onClose}>✕</button></header>
          <div className="app-dialog__body">{children}</div>
          {footer && <footer className="app-dialog__foot">{footer}</footer>}
        </>
      )}
    </dialog>
  );
}

/** `await confirmDialog({title, body})` → true/false. Optional `reason` input returns the text (required when set). */
export function confirmDialog(o: { title: string; body?: ReactNode; confirm?: string; danger?: boolean; reason?: string }): Promise<false | { reason: string }> {
  return new Promise((resolve) => {
    const host = document.createElement('div');
    document.body.appendChild(host);
    const root = createRoot(host);
    const done = (v: false | { reason: string }) => { resolve(v); setTimeout(() => { root.unmount(); host.remove(); }, 0); };
    function C() {
      const [open, setOpen] = useState(true);
      const [reason, setReason] = useState('');
      const close = (v: false | { reason: string }) => { setOpen(false); done(v); };
      return (
        <Dialog open={open} onClose={() => close(false)} title={o.title}
          footer={<>
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => close(false)}>Back</button>
            <button type="button" className={`btn btn--sm ${o.danger ? 'btn--primary' : 'btn--ink'}`} disabled={!!o.reason && reason.trim().length < 3} onClick={() => close({ reason: reason.trim() })}>{o.confirm ?? 'Confirm'}</button>
          </>}>
          {o.body && <div className="app-dialog__text">{o.body}</div>}
          {o.reason && <label className="app-field"><span>{o.reason}</span><textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} autoFocus /></label>}
        </Dialog>
      );
    }
    root.render(<C />);
  });
}
